<script setup>
import { computed, inject, nextTick, onMounted, ref, watch } from 'vue';

// Props 정의
const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    },
    button: {
        type: String,
        default: ''
    },
    upper: {
        type: Boolean,
        default: false
    },
    maxlength: {
        type: Number,
        default: 4000
    },
    validationCheck: {
        type: Function,
        default: () => ({ isValid: true, message: '' })
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    },
    labelEmpty: {
        type: Boolean,
        default: false
    },
    padding: {
        type: String,
        default: '0.75rem'
    },
    width: {
        type: String,
        default: ''
    }
});

// Emits 정의
const emit = defineEmits(['update:modelValue', 'input', 'click', 'change', 'addressCompleted']);

const isReadonly = computed(() => props.readonly);
const inputValue = ref(props.modelValue);
const ValidationMsg = ref('');
const errMessage = ref(props.errmsg);
const isVisible = computed(() => props.visible);
const kakaoMapInstance = inject('kakaoMapInstance');
const isModalOpen = ref(false);
const modalDetailAddress = ref('');
const selectedMainAddress = ref(''); // 카카오에서 선택된 메인 주소
const isAddressSelected = ref(false); // 주소가 선택되었는지 확인

onMounted(() => {});

watch(
    () => props.modelValue,
    (newVal) => {
        if (props.upper) {
            newVal = newVal.toUpperCase();
        }

        inputValue.value = newVal || '';
    },
    { immediate: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);

const onInput = (event) => {
    const oldValue = event.target.value;
    const oldLength = oldValue.length;
    let cursorPosition = event.target.selectionStart;
    let value = event.target.value;
    let validationResult = { isValid: true, message: '' };
    ValidationMsg.value = '';
    errMessage.value = '';

    if (value.length > props.maxlength) {
        value = value.slice(0, props.maxlength);
    }

    const beforeFormatLength = value.length;

    if (props.upper) {
        value = value.toUpperCase();
    }

    const afterFormatLength = value.length;
    const lengthDiff = afterFormatLength - beforeFormatLength;

    // 커서가 값의 길이를 초과하지 않도록 보장
    cursorPosition = Math.min(cursorPosition, afterFormatLength);

    if (typeof props.validationCheck === 'function') {
        try {
            validationResult = props.validationCheck(value) || validationResult;
        } catch (error) {
            console.error('Validation check error:', error);
        }
    }

    ValidationMsg.value = validationResult.isValid ? ValidationMsg.value : validationResult.message;
    emitUpdate(value, 'input');

    nextTick(() => {
        event.target.value = value;
        event.target.setSelectionRange(cursorPosition, cursorPosition);
    });
};

const emitUpdate = (value, event) => {
    if (props.mask === 'phone' || props.mask === 'business') value = value.replaceAll('-', '');
    if (props.number) value = value.replaceAll(',', '');
    inputValue.value = value;
    emit('update:modelValue', value);
    emit('input', {
        id: props.inputId,
        event: event,
        value: value,
        validation: ValidationMsg.value === '' ? true : false
    });
};

const onButtonClick = (event) => {
    if (props.readonly) return;
    isModalOpen.value = true;
    selectedMainAddress.value = '';
    modalDetailAddress.value = '';
    isAddressSelected.value = false;
};

const closeModal = () => {
    isModalOpen.value = false;
    modalDetailAddress.value = '';
    selectedMainAddress.value = '';
    isAddressSelected.value = false;
    postcodeInstance.value = null;
};

const confirmAddress = () => {
    if (!selectedMainAddress.value) {
        alert('주소를 먼저 선택해주세요.');
        return;
    }

    // 메인 주소와 상세 주소를 조합
    const fullAddress = modalDetailAddress.value ? `${selectedMainAddress.value} ${modalDetailAddress.value}` : selectedMainAddress.value;

    // input에 전체 주소 표시
    emitUpdate(fullAddress, 'address');
    ValidationMsg.value = '';
    errMessage.value = '';

    // 부모 컴포넌트에 주소 정보 전달
    emit('addressCompleted', {
        address: selectedMainAddress.value,
        detailAddress: modalDetailAddress.value,
        fullAddress: fullAddress
    });

    closeModal();
};

const postcodeInstance = ref(null);

const execDaumPostcode = () => {
    //console.log('window.daum:', window.daum);

    if (window.daum && window.daum.Postcode) {
        const container = document.getElementById('postcode-container-modal');

        if (!container) {
            console.error('postcode-container-modal 엘리먼트를 찾을 수 없습니다.');
            return;
        }

        // 기존 인스턴스가 있다면 제거
        if (postcodeInstance.value) {
            try {
                container.innerHTML = '';
            } catch (error) {
                console.log('기존 인스턴스 제거 중 오류:', error);
            }
        }

        try {
            postcodeInstance.value = new window.daum.Postcode({
                oncomplete: function (data) {
                    let addr = data.userSelectedType === 'R' ? data.roadAddress : data.jibunAddress;
                    let extraAddr = '';

                    if (data.userSelectedType === 'R') {
                        if (data.bname !== '' && /[동|로|가]$/g.test(data.bname)) {
                            extraAddr += data.bname;
                        }
                        if (data.buildingName !== '' && data.apartment === 'Y') {
                            extraAddr += extraAddr !== '' ? ', ' + data.buildingName : data.buildingName;
                        }
                        if (extraAddr !== '') {
                            extraAddr = ' (' + extraAddr + ')';
                        }
                    }

                    selectedMainAddress.value = addr + extraAddr;
                    isAddressSelected.value = true;
                    //console.log('선택된 주소:', selectedMainAddress.value);

                    hidePostcodeContainer();

                    nextTick(() => {
                        const detailInput = document.querySelector('.detail-address-input');
                        if (detailInput) detailInput.focus();
                    });
                },
                onclose: function (state) {
                    if (state === 'COMPLETE_CLOSE') {
                        hidePostcodeContainer();
                    }
                },
                width: '100%',
                height: '100%',
                embed: container,
                autoClose: false
            });

            postcodeInstance.value.embed(container);
        } catch (error) {
            console.error('Daum Postcode 생성 중 오류:', error);
        }
    } else {
        console.error('Daum Postcode script not loaded');
        // console.log(
        //     'Available window properties:',
        //     Object.keys(window).filter((key) => key.includes('daum') || key.includes('Daum'))
        // );
        alert('주소 검색 서비스를 불러오는 데 실패했습니다. 잠시 후 다시 시도해주세요.');
    }
};

const hidePostcodeContainer = () => {
    const container = document.getElementById('postcode-container-modal');
    if (container) {
        container.style.display = 'none';
    }
};

const showPostcodeContainer = () => {
    const container = document.getElementById('postcode-container-modal');
    if (container) {
        container.style.display = 'block';
        container.innerHTML = '';
    }
};

const resetAddressSearch = () => {
    selectedMainAddress.value = '';
    isAddressSelected.value = false;
    showPostcodeContainer();
    nextTick(() => {
        execDaumPostcode();
    });
};

watch(isModalOpen, (isOpen) => {
    if (isOpen) {
        nextTick(() => {
            showPostcodeContainer();
            setTimeout(() => {
                execDaumPostcode();
            }, 100);
        });
    }
});

const clearInput = () => {
    ValidationMsg.value = '';
    errMessage.value = '';
    emitUpdate('', 'input');
};
</script>

<template>
    <div
        v-if="isVisible"
        class="flex flex-col w-full"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <label v-if="props.label.toLowerCase() !== 'empty' && !labelEmpty" class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
        </label>

        <div class="relative flex items-center h-12 w-full">
            <input
                v-if="isVisible"
                :id="inputId"
                v-model="inputValue"
                class="p-inputtext block h-[2.5rem] ps-5 w-full input-font text-gray-900 border border-gray-300 rounded-lg bg-gray-60 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500"
                :class="{
                    'pr-24': !isReadonly,
                    'error-border': ValidationMsg,
                    'readonly-Bg': isReadonly
                }"
                :placeholder="'주소를 검색하세요.'"
                :required="required"
                :readonly="isReadonly"
                @input="onInput"
            />

            <div class="absolute right-0 top-1/2 transform -translate-y-1/2 flex items-center">
                <button v-if="inputValue && !isReadonly" @click="clearInput" class="clear-button mr-[calc(5.5rem)]">&times;</button>

                <button v-if="!isReadonly" @click="onButtonClick" class="button-style">주소찾기</button>
            </div>

            <!-- 모달 팝업 -->
            <div v-if="isModalOpen" class="postcode-modal-overlay" @click.self="closeModal">
                <div class="postcode-modal-content">
                    <div class="modal-header">
                        <h3 class="modal-title">주소 찾기</h3>
                        <button @click="closeModal" class="close-button">&times;</button>
                    </div>

                    <div id="postcode-container-modal" class="postcode-container"></div>

                    <div v-if="selectedMainAddress" class="selected-address">
                        <div class="address-header">
                            <label class="address-label">선택된 주소:</label>
                            <button @click="resetAddressSearch" class="reset-search-btn">다시 검색</button>
                        </div>
                        <div class="address-display">{{ selectedMainAddress }}</div>
                    </div>

                    <div v-if="selectedMainAddress" class="detail-address-section">
                        <label class="address-label">상세주소 (선택입력):</label>
                        <input v-model="modalDetailAddress" class="detail-address-input" placeholder="상세주소를 입력하세요 (예: 101동 201호)" :disabled="!isAddressSelected" />
                    </div>

                    <!-- 확인 버튼 -->
                    <button @click="confirmAddress" class="confirm-button" :disabled="!selectedMainAddress">주소 입력</button>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.input-wrapper {
    display: flex;
    flex-direction: column;
}

.label {
    font-family: 'SCoreDream600';
    display: flex;
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
    white-space: nowrap;
    user-select: none;
}

.required-asterisk {
    color: #f87171;
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.input-font {
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
}

input:focus:not([readonly]) {
    border-color: #0038ea;
    outline: none;
}

input[readonly]:focus {
    border-color: #d1d5db;
    outline: none;
}

.error-border {
    border: 1px solid red !important;
}

.readonly-Bg {
    background-color: whitesmoke !important;
}

.button-style {
    font-family: 'SCoreDream500';
    font-size: 0.85rem;
    transition: color 0.3s ease;
    border-radius: 0.5rem;
    background-color: #f1f1f1;
    color: #4b5563;
    align-items: center;
    position: absolute;
    right: 0.5rem;
    width: 4.5rem;
    height: 1.8rem;
    border: none;
    cursor: pointer;
}

.button-style:hover {
    background-color: #dddee0;
}

.button-style:focus {
    outline: none;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
    white-space: nowrap;
}

.clear-button {
    cursor: pointer;
    background: transparent;
    border: none;
    font-size: 0.9rem;
    color: #6b7280;
    position: absolute;
    right: 0.1rem;
}

.clear-button:hover {
    color: #4b5563;
}

.postcode-modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1002;
}

.postcode-modal-content {
    background-color: white;
    padding: 24px;
    border-radius: 12px;
    width: 100%;
    min-width: 40rem;
    width: 41rem;
    max-height: 90vh;
    overflow-y: auto;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
}

.modal-title {
    font-size: 1.2rem;
    font-family: 'SCoreDream500';
    color: #374151;
}

.close-button {
    background: none;
    border: none;
    font-size: 24px;
    color: #6b7280;
    cursor: pointer;
    padding: 0;
    width: 30px;
    height: 30px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    transition: background-color 0.2s;
}

.close-button:hover {
    background-color: #f3f4f6;
    color: #374151;
}

.postcode-container {
    width: 100%;
    height: 32.5rem;
    border-radius: 8px;
    margin-bottom: 1rem;
}

.selected-address {
    padding: 1rem;
    background-color: #f9fafb;
}

.address-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 6px;
}

.reset-search-btn {
    background-color: #6b7280;
    color: white;
    border: none;
    padding: 4px 12px;
    border-radius: 4px;
    font-size: 0.8rem;
    cursor: pointer;
    transition: background-color 0.2s;
}

.reset-search-btn:hover {
    background-color: #4b5563;
}

.address-label {
    font-size: 0.875rem;
    font-family: 'SCoreDream500';
    color: #374151;
    display: block;
    margin-bottom: 4px;
}

.address-display {
    font-size: 0.95rem;
    color: #111827;
    font-family: 'SCoreDream400';
}

.detail-address-section {
    margin-bottom: 1rem;
    padding: 1rem;
    background-color: #f9fafb;
}

.detail-address-input {
    width: 100%;
    height: 2.4rem;
    padding-left: 1rem;
    margin-top: 6px;
    border: 0.1rem solid #d1d5db;
    border-radius: 0.375rem;
    font-size: 0.95rem;
    font-family: 'SCoreDream400';
    transition: border-color 0.2s;
    color: black;
}

.detail-address-input:focus {
    outline: none;
    border-color: #0038ea;
}

.detail-address-input:disabled {
    background-color: #f9fafb;
    color: #9ca3af;
    cursor: not-allowed;
}

.confirm-button {
    width: 100%;
    padding: 14px;
    border: none;
    border-radius: 8px;
    background-color: #0038ea;
    color: white;
    font-size: 1rem;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.2s;
    font-family: 'SCoreDream500';
}

.confirm-button:hover:not(:disabled) {
    background-color: #002bb8;
}

.confirm-button:disabled {
    background-color: #d1d5db;
    cursor: not-allowed;
    color: #9ca3af;
}

/* Placeholder 스타일 */
input::-webkit-input-placeholder {
    text-align: left !important;
}
input::-moz-placeholder {
    text-align: left !important;
}
input:-ms-input-placeholder {
    text-align: left !important;
}
input:-moz-placeholder {
    text-align: left !important;
}
</style>
